import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { getMockUsers } from '../mock';
import { masterAPI } from '../services/api';
import { formatCurrency, formatDateTime } from '../utils/currency';
import { formatPhoneNumber } from '../utils/validation';
import { 
  Search, 
  Users, 
  Crown, 
  Shield, 
  ChevronRight,
  Mail,
  Phone,
  CalendarDays,
  TrendingUp,
  Building2,
  Package,
  Eye,
  AlertTriangle,
  Database,
  Edit,
  Trash2,
  UserCog,
  RefreshCw,
  Key,
  Lock,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  FileText,
  User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import PasswordVerificationDialog from '../components/PasswordVerificationDialog';

const MasterData = () => {
  const [users, setUsers] = useState([]);
  const [adminStats, setAdminStats] = useState({});  // Map of adminId -> stats
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordVerifyOpen, setPasswordVerifyOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  // Password Reset Request states
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [masterPassword, setMasterPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  // Custom Requirements states
  const [customRequirements, setCustomRequirements] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [requirementDetailOpen, setRequirementDetailOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    venueName: '',
    role: 'admin'
  });
  const { currentUser } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('token');
      
      // Fetch all users from backend
      const response = await fetch(`${API_URL}/api/master/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const usersData = await response.json();
        setUsers(usersData);
      } else {
        console.error('Failed to load users from backend');
        // Fallback to mock data
        setUsers(getMockUsers());
      }
      
      // Fetch password reset requests
      try {
        const resetResponse = await fetch(`${API_URL}/api/auth/password-reset-requests`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (resetResponse.ok) {
          const resetData = await resetResponse.json();
          setPasswordResetRequests(resetData);
        }
      } catch (err) {
        console.error('Failed to load password reset requests:', err);
      }
      
      // Fetch all admin stats in a single batch call
      try {
        const statsResponse = await fetch(`${API_URL}/api/master/all-admin-stats`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          // Convert array to map for easy lookup
          const statsMap = {};
          statsData.forEach(stat => {
            statsMap[stat.adminId] = stat;
          });
          setAdminStats(statsMap);
        }
      } catch (err) {
        console.error('Failed to load admin stats:', err);
      }

      // Fetch custom requirements from landing page
      try {
        const requirementsResponse = await fetch(`${API_URL}/api/visitor/custom-requirements`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (requirementsResponse.ok) {
          const requirementsData = await requirementsResponse.json();
          setCustomRequirements(requirementsData);
        }
      } catch (err) {
        console.error('Failed to load custom requirements:', err);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Fallback to mock data
      setUsers(getMockUsers());
    }
  };

  // Check if current user is master
  if (!currentUser?.isMaster) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-foreground mb-2">Access Denied</h2>
            <p className="text-gray-600 dark:text-muted-foreground">Only Master account can access Master Data.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Organize users by hierarchy
  const masterUsers = users.filter(u => u.isMaster);
  const adminUsers = users.filter(u => u.role === 'admin');
  const staffUsers = users.filter(u => u.role === 'staff' || u.role === 'manager' || u.role === 'stakeholder');
  const stakeholderUsers = users.filter(u => u.role === 'stakeholder');

  // Get staff for specific admin
  const getStaffForAdmin = (adminId) => {
    return staffUsers.filter(s => s.adminId === adminId);
  };

  // Get statistics for admin from backend data
  const getAdminStats = (adminId) => {
    const adminStaff = getStaffForAdmin(adminId);
    const backendStats = adminStats[adminId] || {};
    
    return {
      totalUsers: 1 + (backendStats.totalStaff || adminStaff.length),
      totalStaff: adminStaff.length,
      staff: adminStaff
    };
  };

  const filteredAdmins = searchQuery 
    ? adminUsers.filter(u => 
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : adminUsers;

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      venueName: user.venueName || '',
      role: user.role || 'admin'
    });
    setEditUserDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    try {
      // Update user via backend API
      await masterAPI.updateUser(editingUser.id, formData);
      
      toast({
        title: 'Success!',
        description: `${formData.name}'s account updated successfully`,
      });
      
      setEditUserDialogOpen(false);
      loadData(); // Reload data
    } catch (error) {
      console.error('Failed to update user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update user',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    // Close delete confirmation and open password verification
    setDeleteDialogOpen(false);
    setPasswordVerifyOpen(true);
  };

  const handlePasswordVerify = async (password) => {
    try {
      // Verify master password via login endpoint
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const verifyResponse = await fetch(`${API_URL}/api/auth/verify-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ password })
      });
      
      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        return { success: false, message: errorData.detail || 'Incorrect password' };
      }

      // Password verified, proceed with deletion
      await masterAPI.deleteUser(userToDelete.id);
      
      toast({
        title: 'Success!',
        description: `${userToDelete.name}'s account deleted`,
      });
      
      setUserToDelete(null);
      loadData(); // Reload data
      return { success: true };
    } catch (error) {
      console.error('Failed to delete user:', error);
      return { success: false, message: error.message || 'Failed to delete user' };
    }
  };

  const handleImpersonate = (user) => {
    // This functionality might already exist via useAuth
    toast({
      title: 'Switching User',
      description: `Logging in as ${user.name}...`
    });
  };

  // Password Reset Request handlers
  const handleResetPasswordClick = (request) => {
    setUserToResetPassword(request);
    setNewPassword('');
    setMasterPassword('');
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }

    if (!masterPassword) {
      toast({
        title: 'Error',
        description: 'Please enter your master password',
        variant: 'destructive'
      });
      return;
    }

    setResetLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/auth/reset-user-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: userToResetPassword.userId,
          newPassword: newPassword,
          masterPassword: masterPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      toast({
        title: 'Success!',
        description: `Password for ${userToResetPassword.userName} has been reset. Please inform them of their new password.`
      });

      setResetPasswordDialogOpen(false);
      setUserToResetPassword(null);
      setNewPassword('');
      setMasterPassword('');
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reset password',
        variant: 'destructive'
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleDismissResetRequest = async (requestId) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/auth/password-reset-requests/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to dismiss request');
      }

      toast({
        title: 'Request Dismissed',
        description: 'The password reset request has been dismissed.'
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to dismiss request',
        variant: 'destructive'
      });
    }
  };

  const pendingResetRequests = passwordResetRequests.filter(r => r.status === 'pending');

  const getRoleBadgeColor = (role) => {
    const colors = {
      master: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 border-purple-200',
      admin: 'bg-red-100 dark:bg-red-900/30 text-red-800 border-red-200',
      manager: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 border-orange-200',
      staff: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border-blue-200'
    };
    return colors[role] || colors.staff;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <Database className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground flex items-center space-x-2">
              <span>Master Data</span>
              <Crown className="w-5 h-5 sm:w-7 sm:h-7 text-purple-600" />
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-muted-foreground mt-1">Complete system overview and user hierarchy</p>
          </div>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          className="flex items-center gap-2 w-full sm:w-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Data
        </Button>
      </div>

      {/* Master Account Info */}
      <Card className="border-0 shadow-md bg-gradient-to-br from-purple-500 via-indigo-600 to-purple-700 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white dark:bg-card/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Crown className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Master Account</h2>
                <p className="text-purple-100">{currentUser?.email}</p>
              </div>
            </div>
            <Badge className="bg-white dark:bg-card/20 text-white border-white/30 px-4 py-2 text-sm">
              INDESTRUCTIBLE
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Password Reset Requests - Show if any pending */}
      {pendingResetRequests.length > 0 && (
        <Card className="border-2 border-amber-300 shadow-md bg-amber-50 dark:bg-amber-900/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800 dark:text-foreground">
              <Key className="w-5 h-5" />
              Password Reset Requests
              <Badge className="bg-amber-50 dark:bg-amber-900/200 text-white ml-2">{pendingResetRequests.length} Pending</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingResetRequests.map((request) => (
              <div key={request.id} className="bg-white dark:bg-card rounded-lg p-4 border border-amber-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-foreground">{request.userName}</span>
                      <Badge variant="outline" className={getRoleBadgeColor(request.userRole)}>
                        {request.userRole}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-muted-foreground flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {request.userEmail}
                    </p>
                    {request.userPhone && (
                      <p className="text-sm text-gray-600 dark:text-muted-foreground flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {request.userPhone}
                      </p>
                    )}
                    {request.venueName && (
                      <p className="text-sm text-gray-600 dark:text-muted-foreground flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {request.venueName}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-muted-foreground flex items-center gap-1 mt-2">
                      <Clock className="w-3 h-3" />
                      Requested: {new Date(request.createdAt).toLocaleString()}
                    </p>
                    {request.reason && (
                      <p className="text-sm text-amber-700 mt-1">
                        <strong>Reason:</strong> {request.reason}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleResetPasswordClick(request)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Lock className="w-4 h-4 mr-1" />
                      Reset Password
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDismissResetRequest(request.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* System Statistics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-foreground mt-1">{users.length}</p>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-muted-foreground">Admins</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{adminUsers.length}</p>
              </div>
              <Shield className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-muted-foreground">Staff</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{users.filter(u => u.role === 'staff').length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-muted-foreground">Stakeholder</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stakeholderUsers.length}</p>
              </div>
              <Users className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-muted-foreground">Bookings</p>
                <p className="text-2xl font-bold text-teal-600 mt-1">
                  {Object.values(adminStats).reduce((sum, s) => sum + (s.totalBookings || 0), 0)}
                </p>
              </div>
              <CalendarDays className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-muted-foreground">Venues</p>
                <p className="text-2xl font-bold text-cyan-600 mt-1">
                  {Object.values(adminStats).reduce((sum, s) => sum + (s.totalVenues || 0), 0)}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-cyan-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
            <Input
              placeholder="Search admins by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Admin Hierarchy View */}
      <Card className="border-0 shadow-md">
        <CardHeader className="bg-gradient-to-r from-red-50 to-pink-50 border-b">
          <CardTitle className="flex items-center space-x-2 dark:text-foreground">
            <Shield className="w-5 h-5 text-red-600" />
            <span>Admin Accounts & Their Teams</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {filteredAdmins.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredAdmins.map((admin) => {
                const stats = getAdminStats(admin.id);
                return (
                  <AccordionItem 
                    key={admin.id} 
                    value={admin.id}
                    className="border rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <AccordionTrigger className="px-4 sm:px-6 py-4 hover:no-underline">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-3 sm:gap-0 sm:pr-4">
                        <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                            {admin.name.charAt(0)}
                          </div>
                          <div className="text-left min-w-0 flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <h3 className="font-bold text-gray-900 dark:text-foreground text-sm sm:text-base truncate">{admin.name}</h3>
                              <Badge className={`${getRoleBadgeColor(admin.role)} text-xs`}>
                                ADMIN
                              </Badge>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground truncate">{admin.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4">
                          <div className="flex items-center gap-2 sm:gap-6 text-xs sm:text-sm">
                            <div className="text-center">
                              <p className="font-bold text-blue-600">{stats.totalStaff}</p>
                              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">Staff</p>
                            </div>
                            <div className="text-center hidden xs:block">
                              <p className="font-bold text-teal-600">{stats.totalBookings}</p>
                              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">Bookings</p>
                            </div>
                            <div className="text-center hidden xs:block">
                              <p className="font-bold text-orange-600">{stats.totalLeads}</p>
                              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">Leads</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditUser(admin)}
                              className="h-7 sm:h-8 px-2 sm:px-3"
                            >
                              <Edit className="w-3 h-3" />
                              <span className="hidden sm:inline ml-1">Edit</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteUser(admin)}
                              className="h-7 sm:h-8 px-2 sm:px-3 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                      <div className="space-y-4 sm:space-y-6 pt-4 border-t">
                        {/* Admin Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                          <Card className="border bg-gray-50 dark:bg-secondary">
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm dark:text-foreground">Admin Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 text-sm">
                              <div className="flex items-center">
                                <Mail className="w-4 h-4 mr-2 text-gray-400 dark:text-muted-foreground" />
                                {admin.email}
                              </div>
                              <div className="flex items-center">
                                <Phone className="w-4 h-4 mr-2 text-gray-400 dark:text-muted-foreground" />
                                {admin.phone}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-muted-foreground mt-2">
                                Created: {formatDateTime(admin.createdAt)}
                              </div>
                              {admin.venueName && (
                                <div className="flex items-center mt-2">
                                  <Building2 className="w-4 h-4 mr-2 text-gray-400 dark:text-muted-foreground" />
                                  <span className="font-medium">{admin.venueName}</span>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>

                        {/* Staff Members */}
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-foreground mb-3 flex items-center">
                            <Users className="w-4 h-4 mr-2" />
                            Staff Members ({stats.totalStaff})
                          </h4>
                          {stats.staff.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {stats.staff.map((staff) => (
                                <Card key={staff.id} className="border bg-blue-50 dark:bg-blue-900/20">
                                  <CardContent className="p-4">
                                    <div className="flex items-start space-x-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold">
                                        {staff.name.charAt(0)}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <p className="font-semibold text-sm text-gray-900 dark:text-foreground truncate">{staff.name}</p>
                                          <Badge className={getRoleBadgeColor(staff.role)} variant="outline">
                                            {(staff.role || 'staff').toUpperCase()}
                                          </Badge>
                                        </div>
                                        <p className="text-xs text-gray-600 dark:text-muted-foreground truncate">{staff.email}</p>
                                        <p className="text-xs text-gray-600 dark:text-muted-foreground">{staff.phone}</p>
                                        {staff.permissions && (
                                          <div className="mt-2 flex flex-wrap gap-1">
                                            {staff.permissions.canEditBookings && (
                                              <Badge variant="outline" className="text-xs">Edit Bookings</Badge>
                                            )}
                                            {staff.permissions.canEditLeads && (
                                              <Badge variant="outline" className="text-xs">Edit Leads</Badge>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 bg-gray-50 dark:bg-secondary rounded-lg border border-dashed">
                              <Users className="w-12 h-12 text-gray-400 dark:text-muted-foreground mx-auto mb-2" />
                              <p className="text-sm text-gray-600 dark:text-muted-foreground">No staff members added yet</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          ) : (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 text-gray-400 dark:text-muted-foreground mx-auto mb-4" />
              <p className="text-gray-600 dark:text-muted-foreground text-lg">No admin accounts found</p>
              <p className="text-gray-500 dark:text-muted-foreground text-sm mt-2">No admins match your search criteria</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Staff Overview */}
      <Card className="border-0 shadow-md">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b p-4 sm:p-6">
          <CardTitle className="flex items-center space-x-2 text-base sm:text-lg dark:text-foreground">
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            <span>All Staff Members ({staffUsers.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {staffUsers.map((staff) => (
              <Card key={staff.id} className="border hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      staff.role === 'manager' ? 'bg-gradient-to-br from-orange-500 to-red-600' : 'bg-gradient-to-br from-blue-500 to-cyan-600'
                    }`}>
                      {staff.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900 dark:text-foreground truncate">{staff.name}</p>
                      <Badge className={getRoleBadgeColor(staff.role)} variant="outline">
                        {(staff.role || 'staff').toUpperCase()}
                      </Badge>
                      <p className="text-xs text-gray-600 dark:text-muted-foreground mt-1 truncate">{staff.email}</p>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">
                        Created: {new Date(staff.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Account</DialogTitle>
            <DialogDescription>Update user details and account information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone * (10 digits)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="XXXXX XXXXX"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
                pattern="[0-9]{5}\s[0-9]{5}"
                maxLength="11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="venueName">Venue Name</Label>
              <Input
                id="venueName"
                value={formData.venueName}
                onChange={(e) => setFormData({ ...formData, venueName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser}>
              <UserCog className="w-4 h-4 mr-2" />
              Update User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        title="Delete User Account?"
        description="This will permanently delete this user and all their data. This action cannot be undone."
        itemName={userToDelete?.name || ''}
        confirmText="Continue"
      />

      {/* Password Verification for Delete */}
      <PasswordVerificationDialog
        open={passwordVerifyOpen}
        onOpenChange={setPasswordVerifyOpen}
        onVerify={handlePasswordVerify}
        title="Master Password Required"
        description={`Please enter your master password to confirm deletion of "${userToDelete?.name || 'this user'}".`}
        userEmail={currentUser?.email}
      />

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Key className="w-6 h-6 text-green-600" />
              </div>
              <DialogTitle className="text-xl">Reset User Password</DialogTitle>
            </div>
            <DialogDescription>
              Set a new password for <strong>{userToResetPassword?.userName}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* User Info */}
            <div className="p-3 bg-gray-50 dark:bg-secondary rounded-lg border border-gray-200 dark:border-border space-y-1">
              <p className="text-sm"><strong>Email:</strong> {userToResetPassword?.userEmail}</p>
              {userToResetPassword?.userPhone && (
                <p className="text-sm"><strong>Phone:</strong> {userToResetPassword?.userPhone}</p>
              )}
              {userToResetPassword?.venueName && (
                <p className="text-sm"><strong>Venue:</strong> {userToResetPassword?.venueName}</p>
              )}
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password *</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Min. 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>

            {/* Master Password Verification */}
            <div className="space-y-2">
              <Label htmlFor="masterPasswordReset">Your Master Password *</Label>
              <Input
                id="masterPasswordReset"
                type="password"
                placeholder="Enter your master password to confirm"
                value={masterPassword}
                onChange={(e) => setMasterPassword(e.target.value)}
              />
            </div>

            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>Important:</strong> After resetting, please inform the user of their new password via phone call or secure message.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setUserToResetPassword(null);
                setNewPassword('');
                setMasterPassword('');
              }}
              disabled={resetLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetLoading || !newPassword || !masterPassword}
              className="bg-green-600 hover:bg-green-700"
            >
              {resetLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Requirements Section */}
      <Card className="border-0 shadow-md mt-6">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 border-b">
          <CardTitle className="flex items-center justify-between dark:text-foreground">
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-5 h-5 text-orange-600" />
              <span className="text-sm sm:text-base">Landing Page - Custom Requirements</span>
              <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 ml-2">{customRequirements.length}</Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              className="text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-6">
          {customRequirements.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No custom requirements submitted yet</p>
              <p className="text-xs text-gray-400 dark:text-muted-foreground mt-1">Requirements from the landing page will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {customRequirements.map((req) => (
                <div
                  key={req.id}
                  className="p-3 sm:p-4 bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedRequirement(req);
                    setRequirementDetailOpen(true);
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-gray-400 dark:text-muted-foreground" />
                        <span className="font-semibold text-gray-800 dark:text-foreground text-sm truncate">{req.name}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {req.phone}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {req.email}
                        </span>
                      </div>
                      {req.venueName && (
                        <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          Venue: {req.venueName}
                        </p>
                      )}
                      {req.requirement && (
                        <p className="text-sm text-gray-700 dark:text-foreground mt-2 line-clamp-2">
                          {req.requirement}
                        </p>
                      )}
                    </div>
                    <div className="flex sm:flex-col items-center sm:items-end gap-2">
                      <Badge className={`text-[10px] ${req.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700' : req.status === 'contacted' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700' : 'bg-green-100 dark:bg-green-900/30 text-green-700'}`}>
                        {req.status?.toUpperCase() || 'PENDING'}
                      </Badge>
                      <span className="text-[10px] text-gray-400 dark:text-muted-foreground">
                        {new Date(req.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Custom Requirement Detail Dialog */}
      <Dialog open={requirementDetailOpen} onOpenChange={setRequirementDetailOpen}>
        <DialogContent className="max-w-md sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-600" />
              Custom Requirement Details
            </DialogTitle>
          </DialogHeader>
          {selectedRequirement && (
            <div className="space-y-4">
              {/* Contact Info */}
              <div className="bg-gray-50 dark:bg-secondary rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-muted-foreground">Name</span>
                  <span className="font-semibold text-gray-800 dark:text-foreground">{selectedRequirement.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-muted-foreground">Phone</span>
                  <a href={`tel:${selectedRequirement.phone}`} className="font-semibold text-blue-600 hover:underline">
                    {selectedRequirement.phone}
                  </a>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-muted-foreground">Email</span>
                  <a href={`mailto:${selectedRequirement.email}`} className="font-semibold text-blue-600 hover:underline text-sm">
                    {selectedRequirement.email}
                  </a>
                </div>
                {selectedRequirement.venueName && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-muted-foreground">Venue Name</span>
                    <span className="font-semibold text-gray-800 dark:text-foreground">{selectedRequirement.venueName}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-muted-foreground">Submitted On</span>
                  <span className="text-sm text-gray-700 dark:text-foreground">
                    {new Date(selectedRequirement.createdAt).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {/* Requirement Text */}
              {selectedRequirement.requirement && (
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200">
                  <p className="text-sm font-medium text-orange-800 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Requirement Details
                  </p>
                  <p className="text-gray-700 dark:text-foreground whitespace-pre-wrap">{selectedRequirement.requirement}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.location.href = `tel:${selectedRequirement.phone}`}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.location.href = `mailto:${selectedRequirement.email}`}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                <Button
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  onClick={() => setRequirementDetailOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterData;
